<script setup lang="ts">
import { computed, ref } from "vue";
import { useSystemHistory } from "../composables/useHistory";
import { formatGrouped, formatNumber, formatUptime, MISSING } from "../format";
import type { MetricId } from "../metrics";
import { bucketAxis, fillAxis, SPARK_SECONDS } from "../series";
import type { SystemSnapshot } from "../types/api";
import AppIcon from "./AppIcon.vue";
import Collapse from "./Collapse.vue";
import MetricPanel from "./MetricPanel.vue";
import SectionBlock from "./SectionBlock.vue";
import Sparkline from "./Sparkline.vue";

const props = defineProps<{ system: SystemSnapshot; tableFullRecent: number }>();

/** Which history chart is open, if any. One at a time keeps the section short. */
const selected = ref<MetricId | null>(null);
const moreOpen = ref(false);
const PRIMARY: ReadonlyArray<MetricId> = ["cpu", "memory", "disk", "network"];

function select(id: MetricId) {
  selected.value = selected.value === id ? null : id;
}

const sparkEntries = useSystemHistory(
  ["cpu_percent", "memory_percent", "rx_kbps", "tx_kbps"],
  "24h",
  SPARK_SECONDS
);
const sparks = computed(() => {
  const resolution = Math.max(SPARK_SECONDS, ...sparkEntries.value.map((entry) => entry.resolution));
  const axis = bucketAxis(
    sparkEntries.value.flatMap((entry) => entry.points.map((point) => point.timestamp)),
    resolution
  );
  const [cpu, memory, rx, tx] = sparkEntries.value.map((entry) =>
    fillAxis(axis, entry.points, (point) => point.value)
  );
  const network = rx.map((value, index) => {
    const other = tx[index];
    return value == null && other == null ? null : (value ?? 0) + (other ?? 0);
  });
  return { cpu, memory, network };
});

/** A disk filling up is the one tile reading that calls for action on its own. */
const diskLevel = computed(() =>
  props.system.disk_percent >= 90 ? "bad" : props.system.disk_percent >= 80 ? "warn" : "ok"
);

const percentText = (value: number | null, digits = 0) =>
  value == null ? MISSING : value.toFixed(value < 10 ? Math.max(digits, 1) : digits);

/** Both directions in one unit, chosen by the busier one, so the pair compares at a glance. */
const network = computed(() => {
  const { rx_kbps: rx, tx_kbps: tx } = props.system;
  const mega = Math.max(rx ?? 0, tx ?? 0) >= 1000;
  const text = (value: number | null) =>
    value == null ? MISSING : mega ? (value / 1000).toFixed(1) : value.toFixed(0);
  return { rx: text(rx), tx: text(tx), unit: mega ? "Mbit/s" : "kbit/s" };
});

interface Issue {
  metric: MetricId;
  level: "warn" | "bad";
  text: string;
}

/** Kernel and network-stack readings that are outside their normal range right now. */
const issues = computed<Issue[]>(() => {
  const system = props.system;
  const list: Issue[] = [];
  const conntrack = system.conntrack_percent ?? 0;
  if (props.tableFullRecent > 0) {
    list.push({ metric: "tableFull", level: "bad", text: `连接跟踪表已满 ${props.tableFullRecent} 次` });
  }
  if (conntrack >= 85) list.push({ metric: "conntrack", level: "bad", text: `连接跟踪表已用 ${conntrack.toFixed(0)}%` });
  else if (conntrack >= 70) list.push({ metric: "conntrack", level: "warn", text: `连接跟踪表已用 ${conntrack.toFixed(0)}%` });
  if ((system.tcp_retrans_percent ?? 0) >= 5) {
    list.push({ metric: "retrans", level: "warn", text: `TCP 重传率 ${formatNumber(system.tcp_retrans_percent, 1)}%` });
  }
  if ((system.tcp_listen_overflows ?? 0) > 0) {
    list.push({ metric: "overflows", level: "warn", text: `accept 队列溢出 ${system.tcp_listen_overflows} 次` });
  }
  if ((system.tcp_timewait_overflows ?? 0) > 0) {
    list.push({ metric: "overflows", level: "warn", text: `TIME_WAIT 溢出 ${system.tcp_timewait_overflows} 次` });
  }
  if ((system.tcp_memory_pressures ?? 0) > 0) {
    list.push({ metric: "overflows", level: "warn", text: `TCP 内存压力 ${system.tcp_memory_pressures} 次` });
  }
  return list;
});

const verdict = computed(() => {
  if (!issues.value.length) return { level: "ok", text: "内核与网络栈正常" };
  const level = issues.value.some((issue) => issue.level === "bad") ? "bad" : "warn";
  const extra = issues.value.length > 1 ? `，另有 ${issues.value.length - 1} 项异常` : "";
  return { level, text: `${issues.value[0].text}${extra}` };
});

const issueLevel = (metric: MetricId) =>
  issues.value.find((issue) => issue.metric === metric)?.level ?? null;

const count = (value: number | null | undefined) => formatGrouped(value, 0);
/** A count with its measure word, or just the placeholder when there is nothing to count. */
const counted = (value: number | null | undefined, word: string) =>
  value == null ? MISSING : `${count(value)} ${word}`;

const extras = computed<Array<{ id: MetricId; label: string; value: string; note?: string }>>(() => {
  const system = props.system;
  return [
    { id: "load", label: "负载", value: formatNumber(system.load1, 2), note: "1 分钟平均" },
    { id: "swap", label: "Swap", value: `${formatNumber(system.swap_percent, 0)}%` },
    {
      id: "paging",
      label: "Swap 换页",
      value: `${count(system.swap_in_kib_s)} / ${count(system.swap_out_kib_s)}`,
      note: "换入 / 换出 KiB/s"
    },
    {
      id: "conntrack",
      label: "连接跟踪",
      value: `${formatNumber(system.conntrack_percent, 2)}%`,
      note: `${count(system.conntrack_count)} / ${count(system.conntrack_max)} 条`
    },
    {
      id: "tableFull",
      label: "连接跟踪表满",
      value: counted(system.conntrack_table_full_events, "次"),
      note:
        system.conntrack_table_full_total == null
          ? "本轮采集"
          : `开机以来 ${count(system.conntrack_table_full_total)} 次`
    },
    { id: "retrans", label: "TCP 重传率", value: `${formatNumber(system.tcp_retrans_percent, 2)}%` },
    {
      id: "overflows",
      label: "队列溢出与内存压力",
      value: `${count(system.tcp_listen_overflows)} / ${count(system.tcp_timewait_overflows)} / ${count(system.tcp_memory_pressures)}`,
      note: "accept / TIME_WAIT / 内存"
    },
    { id: "timewait", label: "TIME_WAIT 套接字", value: counted(system.tcp_timewait_count, "个") }
  ];
});

const summaryFacts = computed(() => {
  const system = props.system;
  return [
    `负载 ${formatNumber(system.load1, 2)}`,
    `Swap ${formatNumber(system.swap_percent, 0)}%`,
    `连接跟踪 ${formatNumber(system.conntrack_percent, 2)}%`,
    `TCP 重传 ${formatNumber(system.tcp_retrans_percent, 2)}%`
  ].join(" · ");
});
</script>

<template>
  <SectionBlock title="服务器" :note="`已运行 ${formatUptime(system.uptime_seconds)}`">
    <template #rail><p class="hint">点按任一指标查看走势</p></template>
    <div class="tiles">
      <button class="tile" type="button" :aria-pressed="selected === 'cpu'" @click="select('cpu')">
        <span class="tile__label">CPU</span>
        <span class="tile__value">{{ percentText(system.cpu_percent) }}<span class="unit">%</span></span>
        <Sparkline :values="sparks.cpu" />
      </button>
      <button class="tile" type="button" :aria-pressed="selected === 'memory'" @click="select('memory')">
        <span class="tile__label">内存</span>
        <span class="tile__value">{{ percentText(system.memory_percent) }}<span class="unit">%</span></span>
        <Sparkline :values="sparks.memory" />
      </button>
      <button class="tile" type="button" :aria-pressed="selected === 'disk'" @click="select('disk')">
        <span class="tile__label">磁盘</span>
        <span class="tile__value">{{ percentText(system.disk_percent) }}<span class="unit">%</span></span>
        <span class="tile__meter" :class="`tile__meter--${diskLevel}`" aria-hidden="true">
          <i :style="{ width: `${Math.min(100, Math.max(0, system.disk_percent))}%` }"></i>
        </span>
      </button>
      <button class="tile" type="button" :aria-pressed="selected === 'network'" @click="select('network')">
        <span class="tile__label">网络</span>
        <span class="tile__value tile__value--pair">
          <span><span class="tile__arrow" aria-hidden="true">↓</span><span class="visually-hidden">接收</span>{{ network.rx }}</span>
          <span><span class="tile__arrow" aria-hidden="true">↑</span><span class="visually-hidden">发送</span>{{ network.tx }}</span>
          <span class="unit">{{ network.unit }}</span>
        </span>
        <Sparkline :values="sparks.network" />
      </button>
    </div>

    <Collapse :open="selected != null && PRIMARY.includes(selected)">
      <MetricPanel v-if="selected" :key="selected" :metric="selected" @close="selected = null" />
    </Collapse>

    <button class="more" type="button" :aria-expanded="moreOpen" @click="moreOpen = !moreOpen">
      <span class="dot" :class="`dot--${verdict.level}`" aria-hidden="true"></span>
      <span class="more__verdict">{{ verdict.text }}</span>
      <span class="more__facts num">{{ summaryFacts }}</span>
      <AppIcon class="more__caret" name="chevron-down" :size="14" />
    </button>

    <Collapse :open="moreOpen">
      <div class="extras">
        <button
          v-for="item in extras"
          :key="item.id"
          class="extra"
          type="button"
          :aria-pressed="selected === item.id"
          @click="select(item.id)"
        >
          <span class="extra__label">
            <span v-if="issueLevel(item.id)" class="dot" :class="`dot--${issueLevel(item.id)}`" aria-hidden="true"></span>
            {{ item.label }}
          </span>
          <span class="extra__value num">{{ item.value }}</span>
          <span v-if="item.note" class="extra__note num">{{ item.note }}</span>
        </button>
      </div>
      <Collapse :open="selected != null && !PRIMARY.includes(selected)">
        <MetricPanel v-if="selected" :key="selected" :metric="selected" @close="selected = null" />
      </Collapse>
    </Collapse>
  </SectionBlock>
</template>

<style scoped>
.hint { margin-top: 0.3rem; color: var(--ink-3); font-size: 0.8125rem; line-height: 1.5; }
.tiles { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 0.5rem; margin-inline: -0.75rem; }

.tile {
  display: grid;
  gap: 0.35rem;
  align-content: start;
  min-width: 0;
  padding: 0.75rem;
  border-radius: var(--radius);
  text-align: start;
}
.tile:hover { background: var(--wash); }
.tile[aria-pressed="true"] { background: var(--wash-strong); }
.tile__label { color: var(--ink-3); font-size: 0.8125rem; }
.tile__value { font-size: 1.75rem; font-weight: 500; line-height: 1.15; letter-spacing: -0.02em; white-space: nowrap; }
.tile__value .unit { font-size: 0.875rem; letter-spacing: 0; }
.tile__value--pair { display: flex; flex-wrap: wrap; gap: 0 0.6rem; align-items: baseline; font-size: 1.375rem; line-height: 1.47; }
.tile__arrow { margin-right: 0.15rem; color: var(--ink-3); font-size: 0.875rem; font-weight: 400; }
.tile .sparkline { margin-top: 0.4rem; }

/* The track is a lighter step of the fill's own hue. */
.tile__meter { display: block; height: 0.375rem; margin-top: 1.35rem; border-radius: 999px; background: var(--accent-soft); overflow: hidden; }
.tile__meter i { display: block; height: 100%; border-radius: inherit; background: var(--accent); }
.tile__meter--warn { background: color-mix(in srgb, var(--warn) 24%, var(--bg)); }
.tile__meter--warn i { background: var(--warn); }
.tile__meter--bad { background: color-mix(in srgb, var(--bad) 22%, var(--bg)); }
.tile__meter--bad i { background: var(--bad); }

.more {
  display: flex;
  gap: 0.6rem;
  align-items: center;
  width: calc(100% + 1.5rem);
  min-height: 2.75rem;
  margin: 0.75rem -0.75rem 0;
  padding: 0.35rem 0.75rem;
  border-radius: var(--radius);
  font-size: 0.875rem;
  text-align: start;
}
.more:hover { background: var(--wash); }
.more__verdict { color: var(--ink); white-space: nowrap; }
.more__facts { flex: 1; min-width: 0; overflow: hidden; color: var(--ink-3); font-size: 0.8125rem; text-align: right; text-overflow: ellipsis; white-space: nowrap; }
.more__caret { flex: 0 0 auto; color: var(--ink-3); }
.more[aria-expanded="true"] .more__caret { transform: rotate(180deg); }

.extras { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 0.25rem 0.5rem; margin-inline: -0.75rem; padding-top: 0.25rem; }
.extra { display: grid; gap: 0.1rem; align-content: start; min-width: 0; padding: 0.6rem 0.75rem; border-radius: var(--radius); text-align: start; }
.extra:hover { background: var(--wash); }
.extra[aria-pressed="true"] { background: var(--wash-strong); }
.extra__label { display: flex; gap: 0.4rem; align-items: center; color: var(--ink-3); font-size: 0.8125rem; }
.extra__value { font-size: 1rem; font-weight: 500; white-space: nowrap; }
.extra__note { color: var(--ink-3); font-size: 0.75rem; }

@media (prefers-reduced-motion: no-preference) {
  .more__caret { transition: transform 200ms ease; }
}

@media (max-width: 640px) {
  .tiles, .extras { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .more { flex-wrap: wrap; }
  .more__facts { flex-basis: 100%; order: 3; padding-left: 1.1rem; text-align: left; white-space: normal; }
}
</style>
